import { resolveCookieDomain } from "@/shared/infrastructure/storage/cookies";

export const LANGUAGE_COOKIE_KEY = "avizo_language";
export const DEFAULT_LANGUAGE = "pt-BR";
export const SUPPORTED_LANGUAGES = ["pt-BR", "en"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export function isSupportedLanguage(lang: unknown): lang is SupportedLanguage {
  return typeof lang === "string" && (SUPPORTED_LANGUAGES as readonly string[]).includes(lang);
}

export function getRootCookieDomain(): string | undefined {
  return resolveCookieDomain(import.meta.env.VITE_COOKIE_DOMAIN);
}

/**
 * Reads a cookie value by name.
 */
export function getCookie(name: string): string | null {
  if (typeof document === "undefined" || !document.cookie) return null;

  const cookies = document.cookie.split(/;\s*/);
  for (const item of cookies) {
    const [key, ...rest] = item.split("=");
    if (key.trim() === name) {
      try {
        return decodeURIComponent(rest.join("="));
      } catch {
        return rest.join("=");
      }
    }
  }

  return null;
}

/**
 * Sets a cookie across subdomains.
 */
export function setCookie(name: string, value: string, days = 365): void {
  if (typeof document === "undefined") return;

  const maxAge = days * 24 * 60 * 60;
  const domain = getRootCookieDomain();
  const domainPart = domain ? `; domain=${domain}` : "";

  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax${domainPart}`;
}

function normalizeLanguage(lang: unknown): SupportedLanguage | null {
  if (lang === "pt") return "pt-BR";
  return isSupportedLanguage(lang) ? lang : null;
}

/**
 * Retrieves the currently stored language, prioritizing cookies (for cross-subdomain sharing)
 * with a fallback to localStorage.
 */
export function getStoredLanguage(): SupportedLanguage {
  // 1. Try reading from shared cookies
  const cookieVal = getCookie(LANGUAGE_COOKIE_KEY);
  const normalizedCookie = normalizeLanguage(cookieVal);
  if (normalizedCookie) {
    return normalizedCookie;
  }

  // 2. Fallback to localStorage
  if (typeof localStorage !== "undefined") {
    try {
      const localVal = localStorage.getItem(LANGUAGE_COOKIE_KEY);
      const normalizedLocal = normalizeLanguage(localVal);
      if (normalizedLocal) {
        return normalizedLocal;
      }
    } catch {
      // Ignore localStorage errors
    }
  }

  return DEFAULT_LANGUAGE;
}

/**
 * Stores the chosen language in cookies (shared across subdomains)
 * and syncs to localStorage.
 */
export function setStoredLanguage(language: string): void {
  const langToStore: SupportedLanguage = isSupportedLanguage(language) ? language : DEFAULT_LANGUAGE;

  // Persist to cookie
  setCookie(LANGUAGE_COOKIE_KEY, langToStore);

  // Sync to localStorage
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(LANGUAGE_COOKIE_KEY, langToStore);
    } catch {
      // Ignore localStorage errors
    }
  }
}
